import React from 'react';

/**
 * Bascule à plat — le remplaçant du `Tabs` de `components/`, écarté par #23.
 *
 * #22 puis #40 ont retenu la même forme pour la même raison : des pastilles à
 * plat, jamais un composant d'onglets, parce que 4 des 6 modules de vague 1
 * étaient bâtis sur `Tabs` et qu'aucun ne survit au portage.
 *
 * Sémantique : un groupe de contrôles avec `aria-pressed`, PAS `role="tablist"`.
 * Un tablist promet une relation onglet ⇄ panneau que ces pastilles n'ont pas ;
 * la promettre ferait annoncer au lecteur d'écran une navigation qui n'existe
 * pas.
 */
export interface Pastille<T extends string> {
    valeur: T;
    libelle: string;
}

interface PillsProps<T extends string> {
    /** Nom accessible du groupe. Requis : sans lui, le groupe n'annonce rien. */
    libelle: string;
    options: Pastille<T>[];
    valeur: T;
    onChange: (valeur: T) => void;
    className?: string;
    /**
     * `compact` : pastilles plus petites (filtres d'une liste dans un panneau du site), zone tactile de 44 px
     * gardée sous `lg`.
     */
    taille?: 'normal' | 'compact';
}

const TAILLES = {
    normal: 'min-h-touch px-4 text-sm',
    compact: 'min-h-touch px-2.5 py-1 text-xs lg:min-h-0',
};

export default function Pills<T extends string>({
    libelle,
    options,
    valeur,
    onChange,
    className = '',
    taille = 'normal',
}: PillsProps<T>) {
    return (
        <div
            role="group"
            aria-label={libelle}
            className={`flex flex-wrap items-center ${taille === 'compact' ? 'gap-1.5' : 'gap-2'} ${className}`.trim()}
        >
            {options.map((option) => {
                const actif = option.valeur === valeur;

                return (
                    // mobile-check-ignore — socle : voir Button.tsx
                    <button
                        key={option.valeur}
                        type="button"
                        aria-pressed={actif}
                        onClick={() => onChange(option.valeur)}
                        className={[
                            'inline-flex items-center rounded-full font-semibold',
                            TAILLES[taille],
                            actif
                                ? 'bg-main-color text-white'
                                : 'bg-white text-text-color-light border border-border-color-light',
                        ].join(' ')}
                    >
                        {option.libelle}
                    </button>
                );
            })}
        </div>
    );
}
